import { lastDayOfMonth, format, subMonths } from 'date-fns'

export class ClvrDate extends Date {
    public static BASIC_TIME_FORMAT = 'p'
    public static BASIC_DATE_FORMAT = 'P'
    public static DATE_FORMAT_DB = 'yyyy-MM-dd'
    public static DATETIME_FORMAT_DB = 'yyyy-MM-dd HH:mm:ss'
    public static DATETIME_FORMAT_FILENAME = 'yyyyMMdd_HHmmss'
    public static DATE_FORMAT_DISPLAY_SHORT = 'dd.MM.yyyy'
    public static DATE_FORMAT_FULL_MONTH = 'MMMM yyyy'

    private readonly MONDAY_ID = 1
    private readonly SUNDAY_ID = 7
    /**
     * Return the difference in days between the current weekday and a given one
     * ComparisonWeekday is the destination
     *
     * Ex: weekday is monday (1) and comparison is thursday (4) it will return 3
     */
    public weekdayDifference(comparisonWeekday: number): number {
        if (comparisonWeekday < this.MONDAY_ID || comparisonWeekday > this.SUNDAY_ID) {
            throw new Error(
                `[weekdayDifference] - Unable to compute difference with a weekday outside range [1-7]. Given: ${comparisonWeekday}`,
            )
        }
        const diff = this.getDay() - comparisonWeekday
        return diff > 0 ? 7 - diff : Math.abs(diff)
    }

    /**
     * Retrieve the number in days between the current month days and a given one
     * ComparisonMonthDay is the destination and must be between 1-31
     */
    public monthDayDifference(comparisonMonthDay: number): number {
        if (comparisonMonthDay < 1 || comparisonMonthDay > 31) {
            throw new Error(
                `[monthDayDifference] - Unable to compute difference with a month day outside range [1-31]. Given: ${comparisonMonthDay}`,
            )
        }
        const monthDay = this.getDate()
        const diff = comparisonMonthDay - monthDay

        const currentMonthDayNumber = lastDayOfMonth(this).getDate()

        if (diff > 0 && currentMonthDayNumber >= comparisonMonthDay) {
            // the comparisonMonthDay exist in the current month
            return diff
        }

        // Ex: if we compare 28 to 31, the diff is 3 but the 31st may not exist for the current month
        // So we must add the month day number left for the current to the comparison month Day
        // Same logic when the difference is negative, it means that we go to the next month
        return currentMonthDayNumber - monthDay + comparisonMonthDay
    }

    /**
     * We start the week by Monday = 1 and Sunday = 7
     */
    public getDay(): number {
        return super.getDay() || this.SUNDAY_ID
    }

    /**
     * Compares the current date with dateToCheck
     * Strict mode enables the check on milliseconds and seconds
     *
     * Returns
     * > 0 if 'this' is greater than dateToCheck
     * 0 if both dates are equal
     * < 0 if 'this' is lower than dateToCheck
     */
    public compare(dateToCheck: ClvrDate, strict = false): number {
        if (!strict) {
            const currentCopy = new ClvrDate(this)
            const checkCopy = new ClvrDate(dateToCheck)
            // we remove the seconds and milliseconds
            currentCopy.setSeconds(0)
            checkCopy.setSeconds(0)
            currentCopy.setMilliseconds(0)
            checkCopy.setMilliseconds(0)

            return currentCopy.getTime() - checkCopy.getTime()
        } else {
            return this.getTime() - dateToCheck.getTime()
        }
    }

    public format(dateFormat: string, locale?: Locale): string {
        return format(this, dateFormat, { locale: locale })
    }

    public subMonths(amount: number): ClvrDate {
        return new ClvrDate(subMonths(this, amount))
    }

    public formatToDatetimeDB(): string {
        return format(this, ClvrDate.DATETIME_FORMAT_DB)
    }

    public formatToDatetimeFileName(): string {
        return format(this, ClvrDate.DATETIME_FORMAT_FILENAME)
    }
}
